import React, { createContext, Suspense, lazy, type ComponentType, type FunctionComponent } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { TooltipProvider } from "@/components/ui/tooltip";
import TopNavigation from "./components/TopNavigation";
import { BackfillBanner } from "./features/backfill/BackfillBanner";
import { useGameSync } from "./features/backfill/useGameSync";
import UsernameLink from "./components/UsernameLink";
import { linkModalVisible } from "./features/onboarding/linkPrompt";
import ErrorBoundary from "./components/ErrorBoundary";
import PracticeErrorBoundary from "./pages/practice/PracticeErrorBoundary";
import useChessUsername from "./hooks/useChessUsername";
import { useAuthToken } from "./components/AuthWrapper";
import { MaiaEngineProvider } from "./contexts/MaiaEngineContext";
import { HomeSkeleton } from "./features/home/HomeSkeleton";
import { readLinkedUsername } from "./utils/linkedUsername";
import { loader, useLoaded } from "./hooks/useLoaded";

// ============================================================================
// Pages — each route loads its own chunk when visited, WITHOUT React.lazy
// ============================================================================

// A lazy() page suspends, the route's Suspense fallback commits, and React 19 then holds the page back until 300 ms
// after that fallback (react-dom 19.2: `globalMostRecentFallbackTime + 300 - now()`), the main thread idle between:
// the cached home was ready at ~215 ms and shown at ~516 (2026-10-13); signed out, the landing's chunks were in at 442
// and its hero shown at 670 (speed audit 2026-10-20, #341). So each page module goes through a loader and the route
// renders the "Loading…" placeholder itself, as a plain element, until it's in. Still one chunk per route.
type PageModule = { readonly default: ComponentType };

/** A route's page, from its own chunk: the placeholder until the module is in, then the page. */
function routePage(load: () => Promise<PageModule>): FunctionComponent {
  const page = loader(load);
  return function RoutePage() {
    const mod = useLoaded(page);
    return mod ? <mod.default /> : <PageLoader />;
  };
}

const Review = routePage(() => import("./pages/Review"));
const Play = routePage(() => import("./pages/Play"));
const Library = routePage(() => import("./pages/Library"));
const Drill = routePage(() => import("./pages/Drill"));
const Practice = routePage(() => import("./pages/Practice"));
const Openings = routePage(() => import("./pages/Openings"));
const PlayersShowcase = routePage(() => import("./pages/stats/PlayersShowcase"));
const Profile = routePage(() => import("./pages/Profile"));
const NotFound = routePage(() => import("./pages/NotFound"));
const About = routePage(() => import("./pages/About"));
const Pricing = routePage(() => import("./pages/Pricing"));
const Privacy = routePage(() => import("./pages/Privacy"));
const Terms = routePage(() => import("./pages/Terms"));
// `/` picks one of two (HomeRoute). The home starts at module load for a linked player, so it downloads while the
// rest of the app boots.
const homePage = loader(() => import("./pages/Home"));
const landingPage = loader(() => import("./pages/Landing"));
if (readLinkedUsername()) void homePage.load();
// Dev-only previews. Behind NODE_ENV so the production build drops the imports instead of shipping them: the
// three that used to be static imports put data/openings.js (896 KB raw) into the main bundle, through
// the old Library card preview → … → classifyMoves. Removing them took main from 297 to 167 kB gz
// (perf survey, 2026-10-13).
// Each check is written at the call site on purpose: webpack replaces NODE_ENV, the minifier drops the dead
// branch, and its import() goes with it. Behind a shared helper the import() would sit in an argument and still
// emit a public chunk, which for HomePreview means its fixture of real games (kept out since d79c2321).
const HomePreview = process.env.NODE_ENV === "development"
  ? lazy(() => import("./features/home/HomePreview").then((m) => ({ default: m.HomePreview })))
  : () => null;
const DrillSessionPreview = process.env.NODE_ENV === "development"
  ? lazy(() => import("./features/drill/DrillSessionPreview").then((m) => ({ default: m.DrillSessionPreview })))
  : () => null;
const DrillTabPreview = process.env.NODE_ENV === "development"
  ? lazy(() => import("./features/practice/DrillTabPreview").then((m) => ({ default: m.DrillTabPreview })))
  : () => null;
const LibraryPreview = process.env.NODE_ENV === "development"
  ? lazy(() => import("./features/library/LibraryPreview").then((m) => ({ default: m.LibraryPreview })))
  : () => null;
const CoachFeedbackPreview = process.env.NODE_ENV === "development"
  ? lazy(() => import("./features/review/coach/CoachFeedbackPreview").then((m) => ({ default: m.CoachFeedbackPreview })))
  : () => null;
const LandingPreview = process.env.NODE_ENV === "development"
  ? lazy(() => import("./features/landing/LandingPreview").then((m) => ({ default: m.LandingPreview })))
  : () => null;
const OpeningsPreview = process.env.NODE_ENV === "development"
  ? lazy(() => import("./features/openings/OpeningsPreview").then((m) => ({ default: m.OpeningsPreview })))
  : () => null;

// ============================================================================
// Chess Username Context (typed)
// ============================================================================

interface ChessUsernameContextValue {
  chessUsername: string | null;
  platform: string | null;
  isLinked: boolean;
  isLoading: boolean;
  requestPrompt: () => void;
}

/**
 * Context for the linked chess username.
 * Consumed by pages that need to pre-fill or display the chess username.
 */
export const ChessUsernameContext = createContext<ChessUsernameContextValue>({
  chessUsername: null,
  platform: null,
  isLinked: false,
  isLoading: false,
  requestPrompt: () => {},
});

// ============================================================================
// `/`: the launcher signed in, the landing signed out (#115)
// ============================================================================

/** Signed in → Home, signed out → Landing. While Clerk is still loading, `isSignedIn` reads false, so
 *  branching on it alone would flash the landing at a returning user. A linked username in the local cache
 *  says "probably signed in": those wait (blank) for Clerk; everyone else gets the landing at once, so the
 *  first-time visitor's page never waits on auth. */
function HomeRoute() {
  const { isSignedIn, isAuthReady } = useAuthToken();
  const { isLinked } = React.useContext(ChessUsernameContext);
  // A linked player is almost certainly signed in, so the home renders before Clerk settles: its queries wait
  // for sign-in, but the persisted cache (this account's only, see queryPersister.ts) paints at once, and with
  // nothing cached it shows its skeleton. If Clerk then says signed out, this becomes the landing and
  // CacheOwnerGuard wipes the cache.
  const wantsHome = isAuthReady ? isSignedIn : isLinked;
  const home = useLoaded(homePage, wantsHome);
  const landing = useLoaded(landingPage, !wantsHome);
  // Plain placeholders while a module arrives, not Suspense fallbacks (see routePage).
  if (!wantsHome) return landing ? <landing.default /> : <PageLoader />;
  return home ? <home.default /> : <HomeSkeleton />;
}


// ============================================================================
// Route loading fallback
// ============================================================================

function PageLoader() {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      paddingTop: "120px",
      gap: "12px",
    }}>
      <div style={{
        width: 24,
        height: 24,
        border: "3px solid var(--color-bg-elevated)",
        borderTop: "3px solid var(--color-accent)",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }} />
      <span style={{ color: "var(--color-text-secondary)", fontSize: "14px" }}>
        Loading…
      </span>
    </div>
  );
}

// ============================================================================
// App Content
// ============================================================================

function AppContent() {
  const { pathname } = useLocation();
  const {
    chessUsername,
    platform,
    isLoading,
    isLinked,
    linkPrompt,
    linkUsername,
    dismissPrompt,
    requestPrompt,
  } = useChessUsername();
  // The player's new games, asked for app-wide (#336): on load, when the tab shows or gets focus, every 60 s while it's
  // visible, and for a newly linked username. Here once, so no page asks twice.
  useGameSync({ chessUsername, isLinked });

  return (
    <>
      <TopNavigation chessUsername={chessUsername} platform={platform} onLinkAccount={requestPrompt} />
      {/* Renders null unless a Pro backfill is in flight. */}
      <BackfillBanner />
      <ChessUsernameContext.Provider value={{ chessUsername, platform, isLinked, isLoading, requestPrompt }}>
        {/* Only the dev previews suspend here: the pages show their own placeholder (routePage). */}
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Dev-only shortcut: /testgame always opens a known analyzed game (cabbagelover5566,
                Black) so the Review UI can be eyeballed without importing each time. Guarded on
                NODE_ENV so it never exists in a production build. */}
            {process.env.NODE_ENV === "development" && (
              <Route
                path="/testgame"
                element={<Navigate to="/analyze?gameId=chesscom%3A171996566764&color=black" replace />}
              />
            )}
            {/* Dev-only preview of the drill session (#225) on the home's real games: own mistakes need a signed-in
                account, and the saved Playwright session expires. ?engine=off shows "couldn't check". */}
            {process.env.NODE_ENV === "development" && (
              <Route path="/dev/drill-session" element={<DrillSessionPreview />} />
            )}
            {/* Dev-only preview of the Library (#222) on real games — it needs a signed-in account with analyzed
                games, and the saved Playwright session expires. */}
            {process.env.NODE_ENV === "development" && (
              <Route path="/dev/library" element={<LibraryPreview />} />
            )}
            {/* Dev-only preview of the signed-in home (#115) on real games — Clerk gates the real one, and the saved
                Playwright session expires. */}
            {process.env.NODE_ENV === "development" && (
              <Route path="/dev/home" element={<HomePreview />} />
            )}
            {/* Dev-only preview of the Drill tab (#226) on the home preview's real games — same reason as /dev/home. */}
            {process.env.NODE_ENV === "development" && (
              <Route path="/dev/drill-tab" element={<DrillTabPreview />} />
            )}
            {/* Dev-only preview of the review's thumbs control — it only renders signed in on a coached game, and the
                saved Playwright session expires, so the visual check lives here. */}
            {process.env.NODE_ENV === "development" && (
              <Route path="/dev/coach-feedback" element={<CoachFeedbackPreview />} />
            )}
            {/* Dev-only preview of the Openings page (#235) on a year of real games, as GET /games?view=openings returns
                them — My games needs a signed-in account, and the saved Playwright session expires. */}
            {process.env.NODE_ENV === "development" && (
              <Route path="/dev/openings" element={
                <ErrorBoundary name="Openings">
                  <OpeningsPreview />
                </ErrorBoundary>
              } />
            )}
            {/* Dev-only preview of the signed-out landing with the band file's sample seeded (#287 lands overnight). */}
            {process.env.NODE_ENV === "development" && (
              <Route path="/dev/landing" element={<LandingPreview />} />
            )}
            {/* Public routes */}
            <Route path="/" element={<HomeRoute />} />
            {/* Public info pages — business/team/product legibility (About) + legal (Privacy/Terms) */}
            <Route path="/about" element={<About />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            {/* All routes public — auth gates saving/syncing, not viewing */}
            <Route path="/analyze" element={
              <ErrorBoundary name="Review">
                <Review />
              </ErrorBoundary>
            } />
            {/* Canonical shareable/bookmarkable deep link to a game — /analysis/172871061236.
                Same Review page; useGameImport reads :gameId. /analyze stays for ?fen=/?pgn=/imports. */}
            <Route path="/analysis/:gameId" element={
              <ErrorBoundary name="Review">
                <Review />
              </ErrorBoundary>
            } />
            <Route path="/library" element={
              <ErrorBoundary name="Library">
                <Library />
              </ErrorBoundary>
            } />
            <Route path="/practice" element={
              <PracticeErrorBoundary>
                <Practice />
              </PracticeErrorBoundary>
            } />
            <Route path="/openings" element={
              <ErrorBoundary name="Openings">
                <Openings />
              </ErrorBoundary>
            } />
            <Route path="/patterns" element={<Navigate to="/practice" replace />} />
            <Route path="/stats" element={<Navigate to="/practice" replace />} />
            <Route path="/players" element={
              <ErrorBoundary name="Players">
                <PlayersShowcase />
              </ErrorBoundary>
            } />
            <Route path="/play" element={
              <ErrorBoundary name="Play">
                <Play />
              </ErrorBoundary>
            } />
            <Route path="/drill" element={
              <ErrorBoundary name="Drill">
                <Drill />
              </ErrorBoundary>
            } />
            <Route path="/profile" element={
              <ErrorBoundary name="Profile">
                <Profile />
              </ErrorBoundary>
            } />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </ChessUsernameContext.Provider>

      {/* Username linking modal: when the player asks for it (the account menu, Profile), or after the first sign-in
          if nothing is linked, except on the pages that prompt would hijack (linkPrompt.ts). */}
      {linkModalVisible(linkPrompt, pathname) && (
        <UsernameLink onLink={linkUsername} onDismiss={dismissPrompt} />
      )}
    </>
  );
}

// ============================================================================
// App Root
// ============================================================================

function App() {
  return (
    <TooltipProvider delayDuration={300}>
      <MaiaEngineProvider>
        <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <ErrorBoundary name="App">
            <AppContent />
          </ErrorBoundary>
        </BrowserRouter>
      </MaiaEngineProvider>
    </TooltipProvider>
  );
}

export default App;